/**
 * IndexedDB-backed cache for fal.ai API results.
 *
 * Key: SHA-256 hash of (imageUrl + prompt + model).
 * Value: result URL string.
 *
 * Completely standalone — no dependency on Redux or SpecsEditor models.
 */

const DB_NAME = "falCache";
const STORE_NAME = "results";
const DB_VERSION = 1;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function hashKey(parts: string[]): Promise<string> {
  const data = new TextEncoder().encode(parts.join("|"));
  const hash = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export async function getCached(
  imageUrl: string,
  prompt: string,
  model: string
): Promise<string | null> {
  try {
    const key = await hashKey([imageUrl, prompt, model]);
    const db = await openDb();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const req = tx.objectStore(STORE_NAME).get(key);
      req.onsuccess = () => resolve((req.result as string) ?? null);
      req.onerror = () => resolve(null);
    });
  } catch (e) {
    console.warn("[falCache] getCached failed:", e);
    return null;
  }
}

export async function setCached(
  imageUrl: string,
  prompt: string,
  model: string,
  resultUrl: string
): Promise<void> {
  try {
    const key = await hashKey([imageUrl, prompt, model]);
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).put(resultUrl, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.warn("[falCache] setCached failed:", e);
  }
}

export async function deleteCached(
  imageUrl: string,
  prompt: string,
  model: string
): Promise<void> {
  try {
    const key = await hashKey([imageUrl, prompt, model]);
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.warn("[falCache] deleteCached failed:", e);
  }
}

export async function clearCache(): Promise<void> {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.warn("[falCache] clearCache failed:", e);
  }
}
